import { useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register the ScrollTrigger plugin
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

export const useGSAPAnimations = () => {
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const featureBlocksRef = useRef<HTMLDivElement[]>([]);
  const contextRef = useRef<gsap.Context | null>(null);

  // Function to add feature block refs - memoized to prevent re-renders
  const addFeatureBlockRef = useCallback((el: HTMLDivElement | null) => {
    if (el && !featureBlocksRef.current.includes(el)) {
      featureBlocksRef.current.push(el);
    }
  }, []);

  useEffect(() => {
    // Skip animations on server side
    if (typeof window === 'undefined') return;
    
    // Check for slow connection or reduced motion preference
    const connection = (navigator as any).connection;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isSlowConnection = connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g');
    
    if (prefersReducedMotion || isSlowConnection) {
      return;
    }

    // Defer animations to avoid blocking initial render
    const hoverCleanups: Array<() => void> = [];
    const timeoutId = setTimeout(() => {
      // Safety check - don't create context if component already unmounted
      if (!mainContainerRef.current && !heroTitleRef.current && featureBlocksRef.current.length === 0) {
        return;
      }

      try {
        contextRef.current = gsap.context(() => {
          // 1. Faster page load animation
          if (mainContainerRef.current) {
            gsap.from(mainContainerRef.current, {
              opacity: 0,
              y: 10,
              duration: 0.6,
              ease: "power2.out"
            });
          }

          // 2. Faster hero title animation
          if (heroTitleRef.current) {
            gsap.from(heroTitleRef.current, {
              scale: 0.95,
              opacity: 0,
              duration: 0.5,
              delay: 0.1,
              ease: "power2.out"
            });
          }

          // 3. Faster feature sections with reduced delays
          featureBlocksRef.current.forEach((block, index) => {
            if (block) {
              // Ensure block is visible by default (fallback)
              gsap.set(block, { opacity: 1, y: 0 });
              
              // Only animate on larger screens to avoid mobile issues
              if (window.innerWidth >= 768) {
                gsap.from(block, {
                  y: 20,
                  opacity: 0,
                  duration: 0.4,
                  delay: index * 0.05,
                  ease: "power2.out",
                  scrollTrigger: {
                    trigger: block,
                    start: "top 95%",
                    end: "bottom 5%",
                    toggleActions: "play none none reverse",
                    once: false
                  }
                });
              }
            }
          });

          // 4. Simplified button hover effects (only for desktop)
          if (window.matchMedia('(hover: hover)').matches) {
            const standardButtons = document.querySelectorAll('button, [role="button"]');
            standardButtons.forEach((button) => {
              const buttonEl = button as HTMLElement;
              
              const handleMouseEnter = () => {
                gsap.to(buttonEl, {
                  scale: 1.02,
                  duration: 0.2,
                  ease: "power2.out"
                });
              };

              const handleMouseLeave = () => {
                gsap.to(buttonEl, {
                  scale: 1,
                  duration: 0.2,
                  ease: "power2.out"
                });
              };

              buttonEl.addEventListener('mouseenter', handleMouseEnter);
              buttonEl.addEventListener('mouseleave', handleMouseLeave);
              // Remembered so cleanup can remove them. These listeners were never
              // removed, and the query is document-wide, so buttons that outlive
              // the page (header, the floating assistant, toasts) gained another
              // pair on every visit to any of the six pages using this hook.
              hoverCleanups.push(() => {
                buttonEl.removeEventListener('mouseenter', handleMouseEnter);
                buttonEl.removeEventListener('mouseleave', handleMouseLeave);
                gsap.killTweensOf(buttonEl);
              });
            });
          }
        });
      } catch (error) {
        console.warn('GSAP animation initialization failed:', error);
      }
    }, 50); // Defer by 50ms to allow initial render

    // Proper cleanup function
    return () => {
      clearTimeout(timeoutId);
      hoverCleanups.forEach((undo) => undo());
      hoverCleanups.length = 0;
      
      // Clean up GSAP context
      if (contextRef.current) {
        try {
          contextRef.current.revert();
        } catch (error) {
          console.warn('GSAP context cleanup failed:', error);
        }
        contextRef.current = null;
      }

      // NO ScrollTrigger.getAll() sweep here. It used to kill every trigger on
      // the page — getAll() is global to GSAP, not scoped to a page or a
      // component, despite the comment that used to sit here claiming
      // otherwise. The revert() above already tears down this hook's triggers,
      // because the scrollTrigger is declared inside the gsap.context() call,
      // and context tracks what it creates. So the sweep could only ever
      // destroy someone ELSE'S triggers: any other component animating on
      // scroll would be silently frozen at its start state (typically
      // opacity: 0, i.e. invisible content) the moment a page using this hook
      // unmounted. Six pages use this hook, so anything added later that
      // scroll-animates would have inherited that bug.

      // Reset feature blocks ref for next mount
      featureBlocksRef.current = [];
    };
  }, []);

  return {
    mainContainerRef,
    heroTitleRef,
    addFeatureBlockRef
  };
};
